/**
 * OpportunitiesTab — Slice C pipeline view.
 *
 * Pipeline stages (spec §17.5):
 *   Identified → Reviewing → AwaitingTiming → Ready → Application → (WonSettled | Lost)
 *
 * Renders open opportunities grouped by stage. Each card shows the loan,
 * value, days-since-creation, the clawback warning where relevant, and a
 * dropdown to advance to the next stage. Advances are clawback-gated on
 * the backend, so attempts to skip Ready/Application inside the 12-month
 * window get auto-downgraded to AwaitingTiming with a reason.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Target, TrendingUp, AlertTriangle, Clock, CheckCircle2, XCircle, Plus, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const STAGES = [
  { key: 'Identified',     label: 'Identified',     icon: Target,          accent: 'slate'   },
  { key: 'Reviewing',      label: 'Reviewing',      icon: TrendingUp,      accent: 'blue'    },
  { key: 'AwaitingTiming', label: 'Awaiting timing',icon: Clock,           accent: 'amber'   },
  { key: 'Ready',          label: 'Ready',          icon: CheckCircle2,    accent: 'emerald' },
  { key: 'Application',    label: 'Application',    icon: TrendingUp,      accent: 'fuchsia' },
];

const TYPE_LABELS = {
  RefinanceToPrime: 'Refinance to Prime',
  Repricing:        'Repricing',
  TopUp:            'Top-up',
  NextPurchase:     'Next purchase',
};

const ACCENT = {
  slate:   { chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', dot: 'bg-slate-400' },
  blue:    { chip: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',     dot: 'bg-blue-500' },
  amber:   { chip: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  emerald: { chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  fuchsia: { chip: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-300', dot: 'bg-fuchsia-500' },
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const OpportunitiesTab = ({ onOpenLoan }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [scope, setScope] = useState('team'); // team | mine
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mine_only: scope === 'mine' ? 'true' : 'false',
        include_closed: showClosed ? 'true' : 'false',
      });
      const [r1, r2] = await Promise.all([
        fetch(`${backendUrl}/api/settled-loans/opportunities?${params}`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/opportunities/stats`, { headers: authHeaders }),
      ]);
      if (r1.ok) setOpps(await r1.json());
      if (r2.ok) setStats(await r2.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, scope, showClosed]);

  useEffect(() => { load(); }, [load]);

  const advance = async (opp, toStage) => {
    setBusy(opp.id);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/opportunities/${opp.id}/advance?to_stage=${toStage}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Advance failed');
      const j = await r.json();
      if (j.gate_reason) {
        toast.warning(`Downgraded to ${j.stage}`, { description: j.gate_reason, duration: 6000 });
      } else {
        toast.success(`Moved to ${j.stage}`);
      }
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const close = async (opp, outcome) => {
    const note = window.prompt(`${outcome === 'WonSettled' ? 'Settled!' : 'Lost'} — close reason (optional):`) || '';
    setBusy(opp.id);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/opportunities/${opp.id}/advance?to_stage=${outcome}${note ? `&note=${encodeURIComponent(note)}` : ''}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Close failed');
      toast.success(outcome === 'WonSettled' ? '🎉 Won — settled' : 'Closed as lost');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  // Group by stage for the columns.
  const byStage = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const o of opps) {
      if (out[o.stage]) out[o.stage].push(o);
    }
    return out;
  }, [opps]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="opportunities-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{ k: 'team', label: 'All' }, { k: 'mine', label: 'Mine' }].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                scope === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`opps-scope-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="text-xs text-slate-600 dark:text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="w-3.5 h-3.5 rounded" data-testid="opps-show-closed" />
          Show closed
        </label>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Open pipeline"  value={stats.open_total}            accent="blue" />
          <StatCard label="Pipeline value" value={fmtMoney(stats.open_value)}  accent="emerald" />
          <StatCard label="Awaiting timing"value={stats.by_stage?.AwaitingTiming?.count ?? 0} accent="amber" />
          <StatCard label="Ready / In app" value={(stats.by_stage?.Ready?.count ?? 0) + (stats.by_stage?.Application?.count ?? 0)} accent="fuchsia" />
        </div>
      )}

      {opps.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl" data-testid="opps-empty">
          <Target className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
          <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">No open opportunities yet</p>
          <p className="text-xs">Triggers will auto-create them at +180d for Specialist loans. You can also add one manually from a loan&apos;s drawer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {STAGES.map((s) => (
            <StageColumn
              key={s.key}
              stage={s}
              items={byStage[s.key] || []}
              busy={busy}
              onAdvance={advance}
              onClose={close}
              onOpenLoan={onOpenLoan}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StageColumn = ({ stage, items, busy, onAdvance, onClose, onOpenLoan }) => {
  const Icon = stage.icon;
  const accent = ACCENT[stage.accent];
  return (
    <div className="bg-slate-50/60 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-3" data-testid={`opps-stage-${stage.key}`}>
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className={`w-2 h-2 rounded-full ${accent.dot}`} />
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">{stage.label}</h4>
        <span className="ml-auto text-[10px] text-slate-500">{items.length}</span>
      </div>
      <div className="space-y-2 min-h-[60px]">
        {items.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic px-2 py-3">No opportunities here.</div>
        ) : items.map((o) => (
          <OppCard key={o.id} opp={o} stage={stage} busy={busy === o.id} onAdvance={onAdvance} onClose={onClose} onOpenLoan={onOpenLoan} />
        ))}
      </div>
    </div>
  );
};

const OppCard = ({ opp, stage, busy, onAdvance, onClose, onOpenLoan }) => {
  const accent = ACCENT[stage.accent];
  const blocked = stage.key === 'AwaitingTiming' && opp.clawback_blocked_until;
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 hover:shadow-md transition-shadow" data-testid="opp-card">
      <div className="flex items-baseline gap-2 mb-1">
        <button
          type="button"
          onClick={() => onOpenLoan?.(opp.loan_id)}
          className="font-mono text-[10px] text-blue-700 dark:text-blue-300 hover:underline shrink-0"
        >
          {opp.loan_number}
        </button>
        <span className="text-xs font-semibold text-slate-900 dark:text-white truncate">{opp.client_name}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide ${accent.chip}`}>
          {TYPE_LABELS[opp.type] || opp.type}
        </span>
        {opp.value > 0 && (
          <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(opp.value)}</span>
        )}
      </div>

      {blocked && (
        <div className="px-2 py-1.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-[10px] text-amber-800 dark:text-amber-300 mb-2 flex items-start gap-1">
          <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Clawback gate until <span className="font-bold">{fmtDate(opp.clawback_blocked_until)}</span> — advance to Ready blocked.</span>
        </div>
      )}

      {opp.notes && (
        <p className="text-[11px] text-slate-500 mb-2 line-clamp-2">{opp.notes}</p>
      )}

      <div className="flex items-center gap-1 flex-wrap">
        {/* Quick-advance to next stage */}
        {stage.key !== 'Application' && (
          <NextStageMenu opp={opp} stage={stage} busy={busy} onAdvance={onAdvance} />
        )}
        <button
          type="button"
          onClick={() => onClose(opp, 'WonSettled')}
          disabled={busy}
          title="Mark as settled / won"
          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 text-[10px] font-semibold disabled:opacity-50"
          data-testid="opp-win-btn"
        >
          <CheckCircle2 className="w-3 h-3" /> Won
        </button>
        <button
          type="button"
          onClick={() => onClose(opp, 'Lost')}
          disabled={busy}
          title="Mark as lost"
          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px] font-semibold disabled:opacity-50"
          data-testid="opp-lose-btn"
        >
          <XCircle className="w-3 h-3" /> Lost
        </button>
      </div>
    </div>
  );
};

const NextStageMenu = ({ opp, stage, busy, onAdvance }) => {
  const [open, setOpen] = useState(false);
  // Valid forward targets from the current stage.
  const targets = useMemo(() => {
    const idx = STAGES.findIndex((s) => s.key === stage.key);
    if (idx < 0) return [];
    return STAGES.slice(idx + 1);
  }, [stage]);
  if (!targets.length) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold disabled:opacity-50"
        data-testid="opp-advance-btn"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />} Advance
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-1 z-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 w-44">
            {targets.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { onAdvance(opp, t.key); setOpen(false); }}
                className="block w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                → {t.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, accent }) => {
  const accents = {
    blue:    'text-blue-700 dark:text-blue-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber:   'text-amber-700 dark:text-amber-300',
    fuchsia: 'text-fuchsia-700 dark:text-fuchsia-300',
  };
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-800 grid place-items-center ${accents[accent]}`}>
        <Target className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className={`text-lg font-bold leading-tight ${accents[accent]}`}>{value}</div>
      </div>
    </div>
  );
};

export default OpportunitiesTab;
